import { ChangeEvent, FocusEvent, useRef, useState } from 'react';
import useQuantityKeyHandlers from '@hooks/useQuantityKeyHandlers';
import Config from '@config';

interface UseQuantityStepperOptions {
  initialQuantity?: number;
  unit: 'pieces' | 'kilogram' | '';
  incrementValue?: number;
  onQuantityChange: (newQuantity: number, prevQuantity: number) => void;
  blurOnlyWhenActive?: boolean;
  stopPropagationWhenActive?: boolean;
  // Optional extra ceiling (e.g. reference-order max) that blocks increases beyond
  // Config.PRODUCT.MAX_AMOUNT. Returning true rejects the change. Decreases are never blocked.
  maxGuard?: (value: number) => boolean;
}

const useQuantityStepper = ({
  initialQuantity = 0,
  unit,
  incrementValue = 1,
  onQuantityChange,
  blurOnlyWhenActive,
  stopPropagationWhenActive,
  maxGuard,
}: UseQuantityStepperOptions) => {
  const [currentQuantity, setCurrentQuantity] = useState(initialQuantity);
  const [previousQuantity, setPreviousQuantity] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onClickHandler = (newQuantity: number) => {
    // Only enforce the upper bounds when increasing — decreasing is always allowed.
    const isDecreasing = newQuantity < currentQuantity;
    if (newQuantity < Config.PRODUCT.MIN_AMOUNT) return;
    if (!isDecreasing && (newQuantity > Config.PRODUCT.MAX_AMOUNT || maxGuard?.(newQuantity))) return;
    setCurrentQuantity(newQuantity);
    onQuantityChange(newQuantity, currentQuantity);
  };

  const { onKeyUpHandler, onKeyDownHandler, inputValue, markQuantity } = useQuantityKeyHandlers({
    inputRef,
    isActive,
    unit: unit || 'pieces',
    onValueChange: onClickHandler,
    blurOnlyWhenActive,
    stopPropagationWhenActive,
  });

  const onBlurHandler = (e: FocusEvent<HTMLInputElement>) => {
    const newQuantity = +parseFloat(e.currentTarget.value).toFixed(1);
    if (previousQuantity !== newQuantity) {
      onQuantityChange(newQuantity, previousQuantity);
    }
    setIsActive(false);
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = +inputValue(e);
    if (value > Config.PRODUCT.MAX_AMOUNT || value < Config.PRODUCT.MIN_AMOUNT) return;
    if (maxGuard?.(value)) return;
    setCurrentQuantity(value);
    setIsActive(previousQuantity !== value);
  };

  const onFocusHandler = (e: FocusEvent<HTMLInputElement>) => {
    setIsActive(true);
    setPreviousQuantity(currentQuantity);
    markQuantity(e);
  };

  return {
    inputRef,
    currentQuantity,
    setCurrentQuantity,
    isActive,
    onClickHandler,
    onBlurHandler,
    onChangeHandler,
    onFocusHandler,
    onKeyUpHandler,
    onKeyDownHandler,
    incrementValue,
  };
};

export default useQuantityStepper;
